import {Pipe, PipeTransform} from '@angular/core';
import {PixKeyType} from '@gatewee/types/banking-info';
import {formatCpfCnpjDisplay} from '../helpers/cpf-cnpj-input.helper';
import {formatPhoneDigits} from '../helpers/pix-key-infer.helper';
import {PixKeyErrorType} from './pix-key.utils';

/** Normalize API type (PixKeyType) to validator type (PixKeyErrorType) for formatting. */
function toErrorType(
  type: PixKeyType | PixKeyErrorType | string,
): PixKeyErrorType | null {
  const t = String(type).toUpperCase();
  if (t === PixKeyErrorType.CPF || t === 'DOCUMENT') return PixKeyErrorType.CPF;
  if (t === PixKeyErrorType.CNPJ) return PixKeyErrorType.CNPJ;
  if (t === PixKeyErrorType.MAIL || t === 'EMAIL') return PixKeyErrorType.MAIL;
  if (t === PixKeyErrorType.PHONE) return PixKeyErrorType.PHONE;
  if (t === PixKeyErrorType.RANDOM_KEY) return PixKeyErrorType.RANDOM_KEY;
  return null;
}

@Pipe({
  name: 'pixKey',
  standalone: false,
})
export class PixKeyPipe implements PipeTransform {
  /**
   * Formats a PIX key for display.
   * @param value The raw PIX key value
   * @param type Optional PixKeyType or PixKeyErrorType (DOCUMENT/CPF/CNPJ, PHONE, MAIL, RANDOM_KEY).
   *             If omitted, formatting is inferred from value (digits → document/phone, @ → email, else as-is).
   */
  transform(
    value: string | undefined | null,
    type?: PixKeyType | PixKeyErrorType | string,
  ): string {
    if (value === undefined || value === null) return '';
    const str = value.toString().trim();
    if (!str) return '';

    const errorType = type ? toErrorType(type) : null;

    if (
      errorType === PixKeyErrorType.CPF ||
      errorType === PixKeyErrorType.CNPJ
    ) {
      return formatCpfCnpjDisplay(str);
    }
    if (errorType === PixKeyErrorType.PHONE) {
      return formatPhoneDigits(str);
    }
    if (errorType === PixKeyErrorType.MAIL) {
      return str;
    }
    if (errorType === PixKeyErrorType.RANDOM_KEY) {
      return str;
    }

    // Infer format when type not provided
    const digitsOnly = str.replace(/\D/g, '');
    if (str.includes('@')) return str;
    if (digitsOnly.length === 11 || digitsOnly.length === 14) {
      return formatCpfCnpjDisplay(str);
    }
    if (digitsOnly.length >= 10 && digitsOnly.length <= 15) {
      return formatPhoneDigits(str);
    }
    return str;
  }
}
